import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ShareButton from '@/components/ShareButton';
import { generateSingleAnalysisWord } from '@/utils/wordGenerator';
import { generatePDFFromHTML } from '@/utils/pdfGenerator';
import { FileText, Download, Share2, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Use environment variable with fallback to current domain
const API_URL = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const HistoryComplete = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user) {
      fetchAnalyses();
    }
  }, [user]);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Please log in to view your history');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      console.log('🔍 Fetching analyses from:', API_URL);
      console.log('🔑 Token exists:', !!token);

      const response = await axios.get(`${API_URL}/analyses`, {
        params: { limit: 100 },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      console.log('✅ Analyses loaded:', response.data.length);
      
      const sorted = (response.data || []).sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      
      setAnalyses(sorted);
    } catch (err) {
      console.error('❌ Error fetching analyses:', err);
      
      if (err.response?.status === 401) {
        localStorage.removeItem('authToken');
        setError('Session expired. Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(`Failed to load history: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (analysis) => {
    try {
      toast.info('Generating PDF...');
      // Create a temporary container with the analysis data
      const container = document.createElement('div');
      container.innerHTML = `
        <div style="padding: 20px;">
          <h1 style="color: #1e293b; margin-bottom: 20px;">Analysis Report</h1>
          <div style="margin-bottom: 15px;">
            <strong>Type:</strong> ${analysis.content_type}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Dominant Emotion:</strong> ${analysis.dominant_emotion}
          </div>
          <div style="margin-bottom: 15px;">
            <strong>Date:</strong> ${new Date(analysis.timestamp).toLocaleString()}
          </div>
          <div style="margin-top: 20px;">
            <strong>Emotions:</strong>
            <div>${Object.entries(analysis.emotions || {}).map(([emotion, score]) => 
              `<div>${emotion}: ${(score * 100).toFixed(1)}%</div>`
            ).join('')}</div>
          </div>
        </div>
      `;
      
      await generatePDFFromHTML(container, `analysis-${analysis.id}.pdf`);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleDownloadWord = async (analysis) => {
    try {
      toast.info('Generating Word document...');
      await generateSingleAnalysisWord(analysis);
      toast.success('Word document downloaded!');
    } catch (error) {
      console.error('Word generation error:', error);
      toast.error('Failed to generate Word document');
    }
  };

  const filteredAnalyses = analyses.filter(a => 
    filter === 'all' || a.content_type === filter
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Loading your history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="text-red-400 text-xl mb-6">{error}</div>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => fetchAnalyses()} className="bg-purple-600 hover:bg-purple-700">
              Retry
            </Button>
            <Button onClick={() => navigate('/login')} className="bg-indigo-600 hover:bg-indigo-700">
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-white">Analysis History</h1>
            <Button onClick={() => navigate('/')} className="bg-indigo-600 hover:bg-indigo-700">
              Back to Home
            </Button>
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'text', 'image', 'video', 'link'].map((type) => (
              <Button
                key={type}
                onClick={() => setFilter(type)}
                variant={filter === type ? 'default' : 'outline'}
                className={filter === type ? 'bg-indigo-600' : ''}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {filteredAnalyses.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-white text-2xl mb-4">
              {filter === 'all' ? 'No analyses yet' : `No ${filter} analyses found`}
            </div>
            <p className="text-gray-400 mb-6">
              Start analyzing content to build your history
            </p>
            <Button onClick={() => navigate('/')} className="bg-indigo-600 hover:bg-indigo-700">
              Start Analyzing
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredAnalyses.map((analysis) => (
              <Card key={analysis.id} className="bg-slate-800 border-slate-700 hover:border-indigo-500 transition-all">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex gap-3 flex-wrap">
                      <Badge className="bg-indigo-600">
                        {analysis.content_type}
                      </Badge>
                      <Badge className="bg-purple-600">
                        {analysis.dominant_emotion}
                      </Badge>
                      {analysis.enhancement && (
                        <Badge className="bg-green-600">Enhanced</Badge>
                      )}
                    </div>
                    <span className="text-gray-400 text-sm">
                      {new Date(analysis.timestamp).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="text-gray-300 mb-4 line-clamp-2">
                    {analysis.content || analysis.content_preview || 'Analysis data'}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={() => navigate(`/analysis/${analysis.id}`)}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                    
                    <Button
                      onClick={() => handleDownloadPDF(analysis)}
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    
                    <Button
                      onClick={() => handleDownloadWord(analysis)}
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Word
                    </Button>
                    
                    <ShareButton
                      analysisId={analysis.id}
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </ShareButton>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryComplete;
